/**
 * Google Tag Manager bootstrap (env-gated).
 * Loads only when REACT_APP_GTM_ID is set at build time.
 * Pushes dataLayer.environment from REACT_APP_ENV for Dev/Staging/Production filtering.
 */

const GTM_ENV_DEVELOPMENT = 'development';
const GTM_ENV_STAGING = 'staging';
const GTM_ENV_PRODUCTION = 'production';

const INIT_FLAG = '__trustvidGtmInitialized';

export function getGtmId() {
  return String(process.env.REACT_APP_GTM_ID || '').trim();
}

export function isGtmEnabled() {
  return Boolean(getGtmId());
}

/**
 * Normalize REACT_APP_ENV aliases to stable GTM dataLayer values.
 * @param {string} [raw]
 * @returns {'development'|'staging'|'production'}
 */
export function normalizeGtmEnvironment(raw) {
  const value = String(raw || '').trim().toLowerCase();
  if (['prod', 'production'].includes(value)) {
    return GTM_ENV_PRODUCTION;
  }
  if (['stage', 'staging', 'stg'].includes(value)) {
    return GTM_ENV_STAGING;
  }
  return GTM_ENV_DEVELOPMENT;
}

export function getGtmEnvironment() {
  return normalizeGtmEnvironment(process.env.REACT_APP_ENV);
}

function buildGtmScriptSrc(containerId) {
  return `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`;
}

function buildGtmNoscriptSrc(containerId) {
  return `https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(containerId)}`;
}

/**
 * Inject official GTM snippets. Idempotent; never throws; never blocks app boot.
 * @returns {boolean} true when GTM was initialized on this call
 */
export function initGtm() {
  try {
    const containerId = getGtmId();
    if (!containerId) {
      return false;
    }
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return false;
    }
    if (window[INIT_FLAG]) {
      return false;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ environment: getGtmEnvironment() });
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });

    const firstScript = document.getElementsByTagName('script')[0];
    const script = document.createElement('script');
    script.async = true;
    script.src = buildGtmScriptSrc(containerId);
    script.setAttribute('data-trustvid-gtm', containerId);
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else if (document.head) {
      document.head.appendChild(script);
    }

    if (document.body) {
      const noscript = document.createElement('noscript');
      noscript.setAttribute('data-trustvid-gtm-noscript', containerId);
      const iframe = document.createElement('iframe');
      iframe.src = buildGtmNoscriptSrc(containerId);
      iframe.height = '0';
      iframe.width = '0';
      iframe.style.display = 'none';
      iframe.style.visibility = 'hidden';
      iframe.title = 'Google Tag Manager';
      noscript.appendChild(iframe);
      document.body.insertBefore(noscript, document.body.firstChild);
    }

    window[INIT_FLAG] = true;
    return true;
  } catch (_err) {
    return false;
  }
}

/** @internal test helper */
export function __resetGtmForTests() {
  if (typeof window !== 'undefined') {
    delete window[INIT_FLAG];
  }
}
